import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';

import {Select} from '@/components';
import {Input, NumberInput} from '@/components/common/Input';
import {
  AfriexCountryCode,
  AfriexCurrencyCode,
  AfriexTransactionChannel,
  AfriexTransactionProcessor,
  AfriexTransactionType,
  BusinessFeeComparator,
  BusinessLimitField,
} from '@/types';
import {formatSelectOptionItem} from '@/utils/merchant';

type FieldValueType =
  | 'STRING'
  | 'NUMBER'
  | 'CURRENCY'
  | 'COUNTRY'
  | 'TRANSACTION_TYPE'
  | 'TRANSACTION_CHANNEL'
  | 'PROCESSOR';

const mapFieldTypeToValue = (field: BusinessLimitField): FieldValueType => {
  switch (field) {
    case BusinessLimitField.FromCurrency:
    case BusinessLimitField.ToCurrency:
      return 'CURRENCY';
    case BusinessLimitField.FromCountry:
    case BusinessLimitField.ToCountry:
      return 'COUNTRY';
    case BusinessLimitField.FromChannel:
    case BusinessLimitField.ToChannel:
      return 'TRANSACTION_CHANNEL';
    case BusinessLimitField.FromProcessor:
    case BusinessLimitField.ToProcessor:
      return 'PROCESSOR';
    case BusinessLimitField.TransactionCount:
    case BusinessLimitField.TransactionVolume:
    case BusinessLimitField.TransactionAmount:
      return 'NUMBER';
    case BusinessLimitField.TransactionType:
      return 'TRANSACTION_TYPE';
    default:
      return 'STRING';
  }
};

const valueTypeToOptionsMap: Partial<Record<FieldValueType, string[]>> = {
  CURRENCY: Object.values(AfriexCurrencyCode),
  COUNTRY: Object.values(AfriexCountryCode),
  TRANSACTION_TYPE: Object.values(AfriexTransactionType),
  TRANSACTION_CHANNEL: Object.values(AfriexTransactionChannel),
  PROCESSOR: Object.values(AfriexTransactionProcessor),
};

const listToOptions = (list: string[]) =>
  list.map((action) => ({
    label: action,
    key: formatSelectOptionItem(action),
    value: action,
  }));

type Props = {
  field: BusinessLimitField;
  comparator: BusinessFeeComparator;
  dataCy?: string;
  handleValueChange: (value: string) => void;
};

export const LimitRuleValueInput = ({
  field,
  comparator,
  dataCy,
  handleValueChange,
}: Props): ReactElement => {
  const valueType = mapFieldTypeToValue(field);
  const shouldRenderTwice = comparator === BusinessFeeComparator.Between;
  const options = valueTypeToOptionsMap[valueType];
  const selectOptions = listToOptions(options ?? []);
  const [firstValue, setFirstValue] = useState<string>('');
  const [secondValue, setSecondValue] = useState<string>('');
  useEffect(() => {
    const valueToUpdate = [firstValue, secondValue].filter(Boolean);
    handleValueChange(valueToUpdate.join(','));
  }, [firstValue, secondValue]);

  useEffect(() => {
    setFirstValue('');
    setSecondValue('');
  }, [valueType]);

  const handleChange = (value: string, isSecond: boolean = false) => {
    if (isSecond) {
      setSecondValue(value);
    } else {
      setFirstValue(value);
    }
  };

  const selectWidget1 = (
    <Select
      dataCy={dataCy ? `${dataCy}-first` : undefined}
      id="field"
      name="field"
      options={selectOptions}
      value={firstValue}
      className="w-full lg:w-fit"
      placeholder="---Select One---"
      onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
        handleChange(event.target.value)
      }
    />
  );
  const selectWidget2 = (
    <Select
      dataCy={dataCy ? `${dataCy}-second` : undefined}
      id="field"
      name="field"
      options={selectOptions}
      value={secondValue}
      className="w-full lg:w-fit"
      placeholder="---Select One---"
      onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
        handleChange(event.target.value, true)
      }
    />
  );

  const numberInputWidget1 = (
    <NumberInput
      data-cy={dataCy ? `${dataCy}-first` : undefined}
      min={0}
      value={firstValue}
      className="w-fit"
      onValueChange={(rawvalue: string) => handleChange(rawvalue)}
    />
  );
  const numberInputWidget2 = (
    <NumberInput
      data-cy={dataCy ? `${dataCy}-second` : undefined}
      min={0}
      value={secondValue}
      className="w-fit"
      onValueChange={(rawvalue: string) => handleChange(rawvalue, true)}
    />
  );

  const textInputWidget1 = (
    <Input
      data-cy={dataCy ? `${dataCy}-first` : undefined}
      value={firstValue}
      className="w-fit"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        handleChange(e.target.value)
      }
    />
  );
  const textInputWidget2 = (
    <Input
      data-cy={dataCy ? `${dataCy}-second` : undefined}
      value={secondValue}
      className="w-fit"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        handleChange(e.target.value, true)
      }
    />
  );

  switch (valueType) {
    case 'CURRENCY':
    case 'COUNTRY':
    case 'TRANSACTION_TYPE':
    case 'TRANSACTION_CHANNEL':
    case 'PROCESSOR':
      return shouldRenderTwice ? (
        <>
          {selectWidget1}
          {selectWidget2}
        </>
      ) : (
        selectWidget1
      );
    case 'NUMBER':
      return shouldRenderTwice ? (
        <>
          {numberInputWidget1}
          {numberInputWidget2}
        </>
      ) : (
        numberInputWidget1
      );
    default:
      return shouldRenderTwice ? (
        <>
          {textInputWidget1}
          {textInputWidget2}
        </>
      ) : (
        textInputWidget1
      );
  }
};
